// src/pages/Painel.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import questions from '../utils/questions';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header_log';
import Metodologia from '../components/painel/Metodologia';
import AnaliseDados from '../components/painel/AnaliseDados';
import AnalysisSection from '../components/AnalysisSection';
import RatingComments from '../components/RatingComments';
import Introducao from '../components/painel/Introducao';
import Conclusao from '../components/painel/Conclusao';
import './Painel.css';
import './print.css';

import { useReactToPrint } from 'react-to-print';

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Title, Tooltip, Legend);

// Se preferir importar a imagem pela pasta /public:
// Coloque o arquivo em public/capa/hero-ceu.png
const capaSrc = `${process.env.PUBLIC_URL || ''}/capa/hero-ceu.png`;
// Alternativa: importar de /src/assets
// import capaImg from '../assets/hero-ceu.png';

function Painel() {
  const [data, setData] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState('Todas');
  const [totalResponses, setTotalResponses] = useState(0);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const { year } = useParams();

  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  // ======= Área alvo da impressão =======
  const printRef = useRef(null);
  const originalTitleRef = useRef(document.title);

  // Converte canvases (se houver) para <img> antes de imprimir
  const convertCanvasToImages = useCallback((rootEl) => {
    const swaps = [];
    if (!rootEl) return () => {};
    const canvases = Array.from(rootEl.querySelectorAll('canvas'));
    canvases.forEach((c) => {
      try {
        const dataUrl = c.toDataURL('image/png', 1.0);
        const img = document.createElement('img');
        img.src = dataUrl;
        img.className = 'chart-print';
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
        c.style.display = 'none';
        c.parentNode?.insertBefore(img, c);
        swaps.push({ canvas: c, img });
      } catch (e) {
        console.error('[PDF] Falha ao converter canvas', e);
      }
    });
    return () => {
      swaps.forEach(({ canvas, img }) => {
        img.remove();
        canvas.style.display = '';
      });
    };
  }, []);

  // v3: contentRef
  const [revertFn, setRevertFn] = useState(null);

  const onBeforeGetContent = useCallback(() => {
    if (!printRef.current) {
      alert('Não encontrei o conteúdo para imprimir. Recarregue a página e tente novamente.');
      throw new Error('printRef.current null');
    }
    // Título dinâmico para sugerir nome do arquivo
    const hoje = new Date();
    const dataBR = hoje.toLocaleDateString('pt-BR');
    const unidade = selectedUnit || 'Todas';
    document.title = `Relatório - ${unidade} - ${dataBR}`;

    const undo = convertCanvasToImages(printRef.current);
    setRevertFn(() => undo);

    return new Promise((resolve) => setTimeout(resolve, 400));
  }, [convertCanvasToImages, selectedUnit]);

  const onAfterPrint = useCallback(() => {
    if (typeof revertFn === 'function') revertFn();
    setRevertFn(null);
    if (originalTitleRef.current) document.title = originalTitleRef.current;
  }, [revertFn]);

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: 'relatorio-painel', // será sobrescrito via document.title
    onBeforeGetContent,
    onAfterPrint,
    removeAfterPrint: true,
    pageStyle: `
      @page { size: A4; margin: 16mm 16mm 20mm 16mm; }
      @media print { body { background: #fff; } }
    `,
  });

  // ======= Carregamento dos dados =======
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    // Se não houver ano nos parâmetros, redirecionar para seleção de ano
    if (!year) {
      navigate('/year-selection');
      return;
    }

    async function fetchData() {
      try {
        const url =
          selectedUnit === 'Todas'
            ? `${baseUrl}/api/processed-responses?year=${year}`
            : `${baseUrl}/api/processed-responses?year=${year}&unit=${encodeURIComponent(selectedUnit)}`;

        const [response, commentResponse] = await Promise.all([
          axios.get(url),
          axios.get('/classified_comments.json'),
        ]);

        setData(response.data);
        setComments(commentResponse.data);

        // Total de respostas do item 1
        let totalItem1 = 0;
        response.data.forEach((questionData) => {
          if (questionData.questionId === '1' || questionData.questionId === 1) {
            totalItem1 += Object.values(questionData.options).reduce((acc, val) => acc + val, 0);
          }
        });
        setTotalResponses(totalItem1);
        setLoading(false);
      } catch (err) {
        console.error('Erro ao buscar os dados da API: ', err);
        setError('Erro ao carregar os dados do painel.');
        setLoading(false);
      }
    }
    fetchData();
  }, [selectedUnit, baseUrl, currentUser, navigate, year]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao realizar logout: ', error);
    }
  };

  if (loading) return <div className="loading">Carregando...</div>;
  if (error) return <div className="error">{error}</div>;

  const handleUnitChange = (event) => setSelectedUnit(event.target.value);

  // ======= Dados estáticos do Relatório =======
  const periodoPesquisa = 'Agosto a Dezembro de 2024';
  const totalAlunos = 5400;
  const percentualAmostra = 15;
  const tamanhoAmostra = 810;
  const metodosAnalise = 'análise estatística descritiva e testes de hipóteses';
  const indiceConfianca = 95;
  const margemErro = 2.5;

  return (
    <div className="painel-container container">
      {/* Ações (fora da área impressa) */}
      <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <button 
          type="button" 
          onClick={() => navigate('/year-selection')}
          style={{ backgroundColor: '#6c757d' }}
        >
          ← Mudar Ano ({year})
        </button>
        <button type="button" onClick={() => handlePrint?.()}>Exportar PDF</button>
      </div>

      {/* Header (não imprime) */}
      <div className="no-print">
        <Header
          selectedUnit={selectedUnit}
          handleUnitChange={handleUnitChange}
          handleLogout={handleLogout}
        />
      </div>

      {/* ======= CONTEÚDO QUE VAI PARA O PDF ======= */}
      <div ref={printRef}>
        {/* CAPA / HERO (aparece na web e imprime antes da Introdução) */}
        <section className="capa-print">
          <img className="capa-img" src={capaSrc} alt="Capa do Relatório" />
          <h1 className="report-title">
            Relatório de Pesquisa de Satisfação — <span className="unit-name">{selectedUnit}</span>
          </h1>
        </section>

        {/* Introdução */}
        <section className="introducao">
          <Introducao
            periodo={periodoPesquisa}
            totalRespostas={totalResponses}
            indiceConfianca={indiceConfianca}
            margemErro={margemErro}
          />
        </section>

        {/* Metodologia (pode quebrar normalmente) */}
        <section className="metodologia procedimentos-eticos">
          <Metodologia
            totalAlunos={totalAlunos}
            percentualAmostra={percentualAmostra}
            tamanhoAmostra={tamanhoAmostra}
            totalRespostas={totalResponses}
            indiceConfianca={indiceConfianca}
            margemErro={margemErro}
          />
        </section>

        {/* Análises e Gráficos — evitar quebra no meio dos blocos */}
        <section className="analise-dados chart-block">
          <AnaliseDados metodosAnalise={metodosAnalise} />
          <div className="chart-block">
            {/* dentro do AnalysisSection, cada .question-block vira “anti-quebra” no print.css */}
            <AnalysisSection data={data} questions={questions} comments={comments} />
          </div>
        </section>

        {/* Comentários — visíveis na tela, ocultos no PDF */}
        <section className="comentarios">
          <RatingComments comments={comments} />
        </section>

        {/* Conclusão */}
        <section className="conclusao">
          <Conclusao />
        </section>
      </div>
    </div>
  );
}

export default Painel;
